import {
  Dialog,
  DialogContent,
  Stack,
} from "@mui/material";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Formik, Form } from "formik";
import Audit from "../../../Images/Audit.png";
import EmployeeProfile from "./EmployeeProfile/EmployeeProfile";
import EmployeeDetails from "./EmployeeDetails/EmployeeDetails";
import { getCorporations, getEmployeeById, deleteEmployee } from "../../../api";
import {
  validateEmployeeForm,
  createDefaultEmployeeValues,
  mapApiDataToEmployeeForm,
} from "./employeeFormDefaults";
import { useEmployeeSubmit } from "./useEmployeeSubmit";
import AuditLog from "../../../AuditLog/AuditLog";
import PayrollsPopup from "../../../Components/PayrollPopup";
import { useAlerts } from "../../../Hooks";
import shortcutManager from "../../../Utilities/ShortcutManager";
import { useParams } from "react-router-dom";
import { FormToolbar } from "../../../Components/FormToolbar";
import { useSnackbar } from "notistack";
import { Loader } from "../../../Components/Loader";
import { employeeManagementStyles } from "../../../styles/employeeManagement.styles";

/**
 * Add / Edit Employee Fullscreen Dialog Component.
 * Coordinates multi-tab employee profile, address, employment, and payroll setups.
 */
const AddNewEmployee = ({
  open,
  onClose,
  employeeId,
  onSuccess,
  permissions,
  isQuickAdd,
  ...props
}) => {
  const { corpId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  // Loading States
  const [detailsLoading, setDetailsLoading] = useState(Boolean(employeeId));
  const [corpLoading, setCorpLoading] = useState(true);
  const [payrollLoading, setPayrollLoading] = useState(false);

  // Form State & Refs
  const [formValues, setFormValues] = useState(null);
  const [editData, setEditData] = useState(null);
  const submitActionRef = useRef("close");
  const formikRef = useRef(null);

  // Toolbar Save Options State
  const [openSaveOptions, setOpenSaveOptions] = useState(false);
  const saveAnchorRef = useRef(null);
  const [selectedSaveIndex, setSelectedSaveIndex] = useState(0);

  // Audit Log State
  const [openAudit, setOpenAudit] = useState(false);

  // Clone Properties State
  const [allCorporations, setAllCorporations] = useState([]);
  const [cloneCorps, setCloneCorps] = useState([]);
  const [isCloning, setIsCloning] = useState(false);

  // Alerts Confirmation Modal
  const {
    popup: alertPopup,
    showErrorPopup,
    hidePopup,
    showDeleteConfirm,
    showCancelConfirm,
  } = useAlerts();

  // Extract specific scalar props to prevent object reference recreation on every render
  const {
    employeeName,
    payFrequency,
    corporationId,
    corporationName,
    defaultDeptId,
    defaultJobId,
    profitCenterId,
  } = props;

  // Pure default values factory - recomputes ONLY when relevant prop values change
  const defaultValues = useMemo(
    () =>
      createDefaultEmployeeValues(
        {
          employeeName,
          payFrequency,
          corporationId,
          corporationName,
          defaultDeptId,
          defaultJobId,
          profitCenterId,
        },
        corpId
      ),
    [
      employeeName,
      payFrequency,
      corporationId,
      corporationName,
      defaultDeptId,
      defaultJobId,
      profitCenterId,
      corpId,
    ]
  );

  const defaultValuesRef = useRef(defaultValues);
  useEffect(() => {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);

  const showErrorPopupRef = useRef(showErrorPopup);
  useEffect(() => {
    showErrorPopupRef.current = showErrorPopup;
  }, [showErrorPopup]);

  // Modular Submission Hook
  const { actionLoading, handleEmployeeSubmit } = useEmployeeSubmit({
    employeeId,
    editData,
    isCloning,
    cloneCorps,
    defaultValues,
    submitActionRef,
    enqueueSnackbar,
    onSuccess,
    onClose,
    setFormValues,
    setEditData,
  });

  const isLoading = detailsLoading || actionLoading || corpLoading || payrollLoading;

  const saveOptions = useMemo(
    () => (isQuickAdd ? ["Save & Close"] : ["Save & New", "Save & Close"]),
    [isQuickAdd]
  );

  // --- Fetch Existing Employee Data for Edit Mode ---
  const fetchEmployeeData = useCallback(async (id) => {
    setDetailsLoading(true);
    try {
      const data = await getEmployeeById(id);
      if (data) {
        const mapped = mapApiDataToEmployeeForm(data, defaultValuesRef.current);
        setFormValues(mapped);
        setIsCloning(Boolean(data.IsCloning || (data.CorpIDs && data.CorpIDs.length > 0)));
        setCloneCorps(data.CorpIDs || []);

        setEditData({
          ID: data.EMPID || data.ID,
          AddressID: data.AddressId || data.SaveAddressReq?.ID,
          ContactID: data.ContactId || data.RelationContactReq?.[0]?.ID,
          RelationContactID: data.RelationContactId || data.RelationContactReq?.[1]?.ID,
          EmergencyContactID: data.RelationContactId || data.RelationContactReq?.[1]?.ID,
          EmploymentDetailID: data.EmploymentDetailId || data.EmploymentDetailsRequest?.ID,
        });
      }
    } catch {
      if (showErrorPopupRef.current) {
        showErrorPopupRef.current("An unexpected error occurred while loading employee details.");
      }
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Sync Form State when dialog opens or target employeeId changes
  useEffect(() => {
    if (!open) return;

    if (employeeId) {
      fetchEmployeeData(employeeId);
    } else {
      setFormValues(defaultValues);
      setEditData(null);
    }
  }, [open, employeeId, defaultValues, fetchEmployeeData]);

  // Fetch Corporations for dropdown selection and cloning
  useEffect(() => {
    if (!open) return;

    const fetchCorps = async () => {
      setCorpLoading(true);
      try {
        const data = await getCorporations();
        const formatted = (data || []).map((c) => ({
          corpName: c.Name,
          corpValue: c.ID,
        }));
        setAllCorporations(formatted);
      } finally {
        setCorpLoading(false);
      }
    };

    fetchCorps();
  }, [open]);

  // --- Delete Employee Action ---
  const handleDeleteEmployee = useCallback(() => {
    if (permissions && !permissions.canDelete) {
      enqueueSnackbar("You don't have permission to delete employees.", { variant: "error" });
      return;
    }

    if (!employeeId) return;

    showDeleteConfirm("Are you sure you want to delete this employee?", async () => {
      try {
        const response = await deleteEmployee(employeeId);
        if (response?.StatusCode === 200) {
          enqueueSnackbar("Employee deleted successfully", { variant: "success" });
          if (onSuccess) onSuccess();
          onClose();
        } else {
          enqueueSnackbar(response?.Status || "Failed to delete employee", { variant: "error" });
        }
      } catch {
        enqueueSnackbar("Something went wrong while deleting employee", { variant: "error" });
      }
    });
  }, [permissions, employeeId, enqueueSnackbar, showDeleteConfirm, onSuccess, onClose]);

  // --- Modal Close Confirmation ---
  const handleClose = useCallback((isDirty) => {
    if (isDirty) {
      showCancelConfirm(
        "Are you sure you want to cancel? All unsaved changes will be lost.",
        onClose
      );
    } else {
      onClose();
    }
  }, [showCancelConfirm, onClose]);

  // --- Save Split-Button Dropdown Handlers ---
  const handleSaveMenuItemClick = useCallback((event, index, submitForm, values) => {
    setSelectedSaveIndex(index);
    setOpenSaveOptions(false);

    const errorMsg = validateEmployeeForm(values);
    if (errorMsg) {
      enqueueSnackbar(errorMsg, { variant: "error" });
      return;
    }

    submitActionRef.current = saveOptions[index] === "Save & New" ? "new" : "close";
    submitForm();
  }, [enqueueSnackbar, saveOptions]);

  const triggerSubmit = useCallback((action, formikInstance) => {
    if (formikInstance) {
      const errorMsg = validateEmployeeForm(formikInstance.values);
      if (errorMsg) {
        enqueueSnackbar(errorMsg, { variant: "error" });
        return;
      }
      submitActionRef.current = action;
      formikInstance.submitForm();
    }
  }, [enqueueSnackbar]);

  const handleSaveToggle = useCallback(() => {
    setOpenSaveOptions((prev) => !prev);
  }, []);

  const handleSaveOptionsClose = useCallback((event) => {
    if (saveAnchorRef.current && saveAnchorRef.current.contains(event?.target)) {
      return;
    }
    setOpenSaveOptions(false);
  }, []);

  // --- Keyboard Shortcuts ---
  useEffect(() => {
    if (!open || alertPopup.open) return;

    shortcutManager.removeAll();

    shortcutManager.addShortcut("alt+c", () => {
      handleClose(formikRef.current?.dirty);
    });

    if (employeeId) {
      shortcutManager.addShortcut("alt+u", () => triggerSubmit("close", formikRef.current));
      shortcutManager.addShortcut("alt+l", handleDeleteEmployee);
    } else {
      shortcutManager.addShortcut("alt+n", () => triggerSubmit("new", formikRef.current));
      shortcutManager.addShortcut("alt+s", () => triggerSubmit("close", formikRef.current));
    }

    return () => shortcutManager.removeAll();
  }, [
    open,
    alertPopup.open,
    employeeId,
    handleClose,
    handleDeleteEmployee,
    triggerSubmit,
  ]);

  // Manage application parent navigation menu visibility
  useEffect(() => {
    if (open && !isQuickAdd) {
      window.parent.postMessage("HideMenu", "*");
    }
    return () => {
      if (!isQuickAdd) window.parent.postMessage("ShowMenu", "*");
    };
  }, [open, isQuickAdd]);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(formikRef.current?.dirty)}
      fullScreen
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {isLoading && <Loader loading={isLoading} />}

      {detailsLoading || !formValues ? null : (
        <Formik
          innerRef={formikRef}
          initialValues={formValues}
          enableReinitialize={true}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={handleEmployeeSubmit}
        >
          {({ submitForm, values, dirty }) => (
            <>
              <FormToolbar
                editData={employeeId}
                title={employeeId ? "Update Employee" : "Add New Employee"}
                selectedSaveIndex={selectedSaveIndex}
                saveOptions={saveOptions}
                handleSaveToggle={handleSaveToggle}
                handleSaveOptionsClose={handleSaveOptionsClose}
                handleSaveMenuItemClick={(event, index) =>
                  handleSaveMenuItemClick(event, index, submitForm, values)
                }
                openSaveOptions={openSaveOptions}
                saveAnchorRef={saveAnchorRef}
                handleUpdate={() => {
                  const errorMsg = validateEmployeeForm(values);
                  if (errorMsg) {
                    enqueueSnackbar(errorMsg, { variant: "error" });
                    return;
                  }
                  submitActionRef.current = "close";
                  submitForm();
                }}
                handleDelete={handleDeleteEmployee}
                handleClose={handleClose}
                dirty={dirty}
                setAuditOpen={() => employeeId && setOpenAudit(true)}
                auditIcon={Audit}
              />

              <DialogContent sx={employeeManagementStyles.dialogContent}>
                <Form style={{ height: "100%", minHeight: 0 }}>
                  <Stack
                    height={"100%"}
                    minHeight={0}
                    direction={"row"}
                    gap={1}
                    overflow="hidden"
                  >
                    {/* Left Column (25%): Basic Profile, Status, Photo & Cloning */}
                    <Stack width={"25%"} gap={2} height={"100%"} minHeight={0}>
                      <EmployeeProfile
                        allCorporations={allCorporations}
                        cloneCorps={cloneCorps}
                        setCloneCorps={setCloneCorps}
                        isCloning={isCloning}
                        setIsCloning={setIsCloning}
                        employeeId={employeeId}
                        loading={corpLoading}
                        corporationId={props.corporationId}
                      />
                    </Stack>

                    {/* Right Column (75%): Tabbed Sub-forms (Address, Employment, Payroll, Attachments) */}
                    <Stack
                      width={"75%"}
                      height={"100%"}
                      minHeight={0}
                      overflow="hidden"
                      border={"1px solid #fff"}
                    >
                      <EmployeeDetails onPayrollLoadingChange={setPayrollLoading} />
                    </Stack>
                  </Stack>
                </Form>
              </DialogContent>
            </>
          )}
        </Formik>
      )}

      <AuditLog
        open={openAudit}
        onClose={() => setOpenAudit(false)}
        auditType="Employee"
        id={employeeId}
      />

      <PayrollsPopup
        open={alertPopup.open}
        popupType={alertPopup.type}
        message={alertPopup.message}
        onConfirm={alertPopup.onConfirm}
        onCancel={hidePopup}
      />
    </Dialog>
  );
};

export default AddNewEmployee;
